import type { SupportTicketStatus } from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import {
  encodeSupportTicketFilterGroup,
  supportTicketFilterData,
  SupportTicketFilterNames,
} from "@redotech/effect-api-shared/schema/support-ticket-filters";
import type { FilterGroup } from "@redotech/redo-views-common/advanced-filters/advanced-filter-data";
import type { ArrayStringFilterData } from "@redotech/redo-views-common/advanced-filters/array-string-filter";
import {
  FilterOperator,
  isFilterGroup,
} from "@redotech/redo-views-common/advanced-filters/generic-advanced-filter-data";
import {
  AI_CONVERSATIONS_VIEW_FILTER_GROUP,
  buildAssignedViewFilterGroup,
  buildMentionsViewFilterGroup,
  DefaultView,
  DRAFTS_VIEW_FILTER_GROUP,
  OPEN_VIEW_FILTER_GROUP,
  withSpamTagExclusion,
} from "./default-views";

export interface SupportTicketCountView {
  viewKey: string;
  filterGroup: string | null;
}

export interface SupportTicketCountSourceView {
  id: string | number;
  filterGroup: FilterGroup | null;
}

export type CountStatusValue = Extract<
  SupportTicketStatus,
  "OPEN" | "CLOSED" | "IN_PROGRESS" | "SNOOZED" | "ARCHIVED" | "DRAFT"
>;

export const COUNT_STATUS_VALUES: CountStatusValue[] = [
  "OPEN",
  "CLOSED",
  "IN_PROGRESS",
  "SNOOZED",
  "ARCHIVED",
  "DRAFT",
];

/**
 * Status values to include in the "All" count.
 * HIDDEN is not included because it's not a user-facing status in the filter toolbar.
 */
export const ALL_COUNT_STATUS_VALUES: CountStatusValue[] = [
  "OPEN",
  "CLOSED",
  "IN_PROGRESS",
  "SNOOZED",
  "ARCHIVED",
  "DRAFT",
];

export function encodeSupportTicketCountFilterGroup(
  filterGroup: FilterGroup | null,
): string | null {
  if (!filterGroup) {
    return null;
  }
  try {
    return encodeSupportTicketFilterGroup(filterGroup);
  } catch {
    return null;
  }
}

export function buildDefaultSupportTicketCountViews(
  userId: string | null,
): SupportTicketCountView[] {
  const views: SupportTicketCountView[] = [
    {
      viewKey: DefaultView.OPEN,
      filterGroup: encodeSupportTicketCountFilterGroup(OPEN_VIEW_FILTER_GROUP),
    },
    {
      viewKey: DefaultView.DRAFTS,
      filterGroup: encodeSupportTicketCountFilterGroup(
        DRAFTS_VIEW_FILTER_GROUP,
      ),
    },
    {
      viewKey: DefaultView.AI_CONVERSATIONS,
      filterGroup: encodeSupportTicketCountFilterGroup(
        AI_CONVERSATIONS_VIEW_FILTER_GROUP,
      ),
    },
  ];

  if (userId) {
    views.push(
      {
        viewKey: DefaultView.ASSIGNED,
        filterGroup: encodeSupportTicketCountFilterGroup(
          buildAssignedViewFilterGroup(userId),
        ),
      },
      {
        viewKey: DefaultView.MENTIONS,
        filterGroup: encodeSupportTicketCountFilterGroup(
          buildMentionsViewFilterGroup(userId),
        ),
      },
    );
  }

  return views;
}

export function buildCustomSupportTicketCountViews(
  views: readonly SupportTicketCountSourceView[],
): SupportTicketCountView[] {
  return views.map((view) => ({
    viewKey: String(view.id),
    filterGroup: encodeSupportTicketCountFilterGroup(
      withSpamTagExclusion(view.filterGroup),
    ),
  }));
}

export function getBaseFilterGroupWithoutStatus(
  filterGroup: FilterGroup,
): FilterGroup | null {
  const clauses: FilterGroup["clauses"] = [];

  for (const clause of filterGroup.clauses) {
    if (isFilterGroup(clause)) {
      const childGroup = getBaseFilterGroupWithoutStatus(clause);
      if (childGroup) {
        clauses.push(childGroup);
      }
    } else if (clause.name !== SupportTicketFilterNames.STATUS) {
      clauses.push(clause);
    }
  }

  return clauses.length === 0 ? null : { ...filterGroup, clauses };
}

export function buildStatusVariantFilterGroup(
  baseFilterGroup: FilterGroup | null,
  status: CountStatusValue,
): FilterGroup {
  const statusFilter: ArrayStringFilterData = {
    ...(supportTicketFilterData[
      SupportTicketFilterNames.STATUS
    ] as ArrayStringFilterData),
    value: [status],
  };

  if (!baseFilterGroup) {
    return { operator: FilterOperator.AND, clauses: [statusFilter] };
  }

  if (baseFilterGroup.operator === FilterOperator.AND) {
    return {
      ...baseFilterGroup,
      clauses: [...baseFilterGroup.clauses, statusFilter],
    };
  }

  return {
    operator: FilterOperator.AND,
    clauses: [baseFilterGroup, statusFilter],
  };
}

export function buildStatusCountViews(
  filterGroup: FilterGroup,
): SupportTicketCountView[] {
  const baseFilterGroup = getBaseFilterGroupWithoutStatus(filterGroup);
  return COUNT_STATUS_VALUES.map((status) => ({
    viewKey: status,
    filterGroup: encodeSupportTicketCountFilterGroup(
      buildStatusVariantFilterGroup(baseFilterGroup, status),
    ),
  }));
}
